/**
 * LEVANTAMIENTO DE WI-FI — la guía, el plano y el resultado.
 * =============================================================================
 *
 * Cuarto rubro. Reusa el lienzo de videovigilancia a través de un adaptador (ver
 * `adaptadores.js`): el trazo, los vértices arrastrables, la máscara que recorta la
 * cobertura al recinto y las cotas por lado son idénticos, y lo que cambia —un punto de
 * acceso no se apunta y tiene un solo alcance— está declarado en un archivo aparte.
 *
 * ---------------------------------------------------------------------------
 * LA HONESTIDAD ES PARTE DEL PRODUCTO AQUÍ, NO UN DESCARGO
 *
 * Este módulo **no predice cobertura**. La propagación de radio en una casa real depende
 * del refrigerador de acero, del espejo del baño y del vecino con su router en el mismo
 * canal, y nada de eso está en un plano. La documentación de Meraki dice que el diseño se
 * valida con un site survey.
 *
 * Lo que sí entrega, y es exactamente lo que hace falta para vender:
 *   · Cuántos puntos de acceso pedir — que es la decisión de compra.
 *   · Dónde ir a medir — que es lo que convierte una visita en un levantamiento.
 *
 * Por eso la pantalla dice «estimado» donde toca. Un cliente al que se le prometió
 * cobertura y no la tiene es un cliente perdido; uno al que se le dijo «esto es la
 * estimación, vamos a medirlo el día de la instalación» es un cliente que entiende por qué
 * hubo que mover un equipo.
 */

import { useState, useMemo } from 'react'
import PasoGuia, { Avance } from './PasoGuia.jsx'
import Materiales from './Materiales.jsx'
import Recinto from './Recinto.jsx'
import { adaptadorWifi } from './adaptadores.js'
import { GUIA_WIFI } from '../motor/guia.js'
import {
  BANDAS, bandaPorId, CONSTRUCCION, PUNTOS_ACCESO, apPorId,
  alcance, calcular, SNR, BORDE_CELDA
} from '../motor/wifi.js'

const MODO_POR_PASO = {
  uso: 'ver', construccion: 'ver', trazo: 'trazo',
  rack: 'rack', colocar: 'colocar', medir: 'ver', calcular: 'ver'
}

export default function Wifi ({ atras, onGuardar, insumos = [], preferencias = {}, semilla = [] }) {
  const [i, setI] = useState(0)

  const [poligono, setPoligono] = useState([])
  const [cerrado, setCerrado] = useState(false)
  const [aps, setAps] = useState(semilla)     // los puntos de acceso colocados en el plano (E7-lite)
  const [rack, setRack] = useState(null)
  const [modelo, setModelo] = useState('ap-interior')
  const [seleccion, setSeleccion] = useState(null)

  const [construccion, setConstruccion] = useState('departamento')
  const [pisos, setPisos] = useState('0')
  const [bandaId, setBandaId] = useState('5')
  const [m2, setM2] = useState('')
  const [dispositivos, setDispositivos] = useState('25')
  const [pisoRuido, setPisoRuido] = useState('-92')
  const [notas, setNotas] = useState('')

  const paso = GUIA_WIFI.pasos[i]
  const modo = MODO_POR_PASO[paso.id] || 'ver'

  const adaptador = useMemo(
    () => adaptadorWifi({ bandaId, construccion, pisos: parseInt(pisos) || 0 }),
    [bandaId, construccion, pisos]
  )

  const r = useMemo(() => {
    if (!cerrado || poligono.length < 3 || !aps.length || !rack || !m2) return null
    return calcular({
      planta: poligono, aps, rack,
      m2: parseFloat(m2),
      dispositivos: parseInt(dispositivos) || 20,
      bandaId, construccion, pisos: parseInt(pisos) || 0,
      pisoRuido: parseFloat(pisoRuido) || -92,
      insumos,
      holguraObra: preferencias.holguraObra ?? 1.10
    })
  }, [poligono, aps, rack, cerrado, m2, dispositivos, bandaId, construccion, pisos,
      pisoRuido, insumos, preferencias.holguraObra])

  const falta = (() => {
    if (paso.id === 'trazo' && poligono.length < 3) return 'Marca al menos tres esquinas.'
    if (paso.id === 'trazo' && !cerrado) return 'Cierra el trazo para continuar.'
    if (paso.id === 'rack' && !rack) return 'Toca el plano donde llega el internet.'
    if (paso.id === 'colocar' && !aps.length) return 'Coloca al menos un punto de acceso.'
    if (paso.id === 'calcular' && !m2) return 'Escribe los metros construidos.'
    return null
  })()

  const apSel = aps.find(a => a.id === seleccion)

  return (
    <div className="app">
      <div className="bar">
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <h1>Wi-Fi
          <span className="crumb">{GUIA_WIFI.duracion} · paso {i + 1} de {GUIA_WIFI.pasos.length}</span>
        </h1>
      </div>

      <main>
        <Avance pasos={GUIA_WIFI.pasos} i={i} onIr={setI} />
        <PasoGuia key={paso.id} paso={paso} />

        {paso.id === 'uso' && (
          <div className="captura">
            <label className="f">
              <span>¿Cuántos aparatos se van a conectar?</span>
              <input type="number" inputMode="numeric" value={dispositivos}
                onChange={e => setDispositivos(e.target.value)} />
              <span>Cuéntalos todos, no sólo los teléfonos: televisiones, cámaras, focos
                    inteligentes, bocinas, tabletas, el timbre. En una casa moderna son
                    muchos más de los que el cliente cree.</span>
            </label>
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Lo que el cliente reporta</span>
              <textarea rows="3" value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Ej. No llega a la recámara del fondo ni al jardín. Las videollamadas se cortan al subir a la planta alta." />
            </label>
          </div>
        )}

        {paso.id === 'construccion' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>¿De qué son los muros?</div>
            <div className="opciones">
              {CONSTRUCCION.map(c => (
                <button key={c.id} type="button"
                  className={`opcion ${construccion === c.id ? 'on' : ''}`}
                  onClick={() => setConstruccion(c.id)}>
                  <b>{c.nombre}</b>
                  <span className="s">{c.que}</span>
                </button>
              ))}
            </div>
            <label className="f" style={{ marginTop: 'var(--e4)', marginBottom: 0 }}>
              <span>Pisos que la señal tiene que atravesar</span>
              <input type="number" inputMode="numeric" value={pisos}
                onChange={e => setPisos(e.target.value)} />
              <span>Cero si todo está en la misma planta. Una losa de concreto cuesta más
                    que varios muros juntos.</span>
            </label>
            {/* La consecuencia de la respuesta, en el momento de darla. Es el Principio 7:
                el operador no tiene por qué saber qué es un coeficiente de la ITU, pero sí
                tiene que ver que su respuesta movió el alcance. */}
            <div className="nota acento" style={{ marginTop: 'var(--e4)' }}>
              Con eso, un punto de acceso de interior alcanza unos{' '}
              <b>{alcance({
                eirp: apPorId('ap-interior').eirp[bandaId],
                mhz: bandaPorId(bandaId).mhz, bandaId, construccion,
                pisos: parseInt(pisos) || 0
              }).metros.toFixed(0)} m</b> en {bandaPorId(bandaId).nombre} antes de caer al
              borde de {BORDE_CELDA} dBm. Es una estimación: se confirma midiendo.
            </div>
          </div>
        )}

        {paso.id === 'trazo' && poligono.length >= 3 && !cerrado && (
          <div className="captura">
            <button className="ib pri w" onClick={() => setCerrado(true)}>Cerrar el trazo</button>
          </div>
        )}

        {paso.id === 'colocar' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>Banda que se está dibujando</div>
            <div className="etapas" style={{ marginBottom: 'var(--e5)' }}>
              {BANDAS.map(b => (
                <button key={b.id} className={`et ${bandaId === b.id ? 'on1' : ''}`}
                  onClick={() => setBandaId(b.id)}>{b.nombre}</button>
              ))}
            </div>
            <div className="nota leible" style={{ marginBottom: 'var(--e4)' }}>
              {bandaPorId(bandaId).que}
            </div>

            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>Qué equipo vas a poner</div>
            <div className="opciones">
              {PUNTOS_ACCESO.map(a => (
                <button key={a.id} type="button"
                  className={`opcion ${modelo === a.id ? 'on' : ''}`}
                  onClick={() => setModelo(a.id)}>
                  <b>{a.nombre}</b>
                  <span className="s">
                    Hasta {a.clientes} aparatos recomendados · {a.watts} W · {a.poe}
                  </span>
                  <span className="s">{a.para}</span>
                </button>
              ))}
            </div>

            {apSel && (
              <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
                <b>{apPorId(apSel.modelo).nombre}</b>, alcance estimado{' '}
                <b>{alcance({
                  eirp: apPorId(apSel.modelo).eirp[bandaId],
                  mhz: bandaPorId(bandaId).mhz, bandaId, construccion,
                  pisos: parseInt(pisos) || 0
                }).metros.toFixed(0)} m</b> en {bandaPorId(bandaId).nombre}.
                <button className="ib sm" style={{ marginTop: 'var(--e3)' }}
                  onClick={() => { setAps(aps.filter(a => a.id !== seleccion)); setSeleccion(null) }}>
                  Quitar este punto de acceso
                </button>
              </div>
            )}
          </div>
        )}

        {paso.id === 'medir' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Piso de ruido medido, en dBm</span>
              <input type="number" inputMode="numeric" value={pisoRuido}
                onChange={e => setPisoRuido(e.target.value)} />
              <span>Va en negativo. Una casa tranquila anda por -92; un edificio con muchos
                    vecinos puede estar en -80 o peor. Es el único número de esta pantalla
                    que NO es estimación, y el que más decide.</span>
            </label>
          </div>
        )}

        {paso.id === 'calcular' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Metros cuadrados construidos</span>
              <input type="number" inputMode="numeric" value={m2}
                onChange={e => setM2(e.target.value)} />
              <span>De aquí sale cuántos equipos pide la cobertura. La otra mitad de la
                    cuenta son los aparatos que capturaste al principio.</span>
            </label>
          </div>
        )}

        <Recinto
          poligono={poligono} setPoligono={setPoligono}
          camaras={aps} setCamaras={setAps}
          rack={rack} setRack={setRack}
          cerrado={cerrado} setCerrado={setCerrado}
          modo={modo} modeloActivo={modelo}
          nivelObjetivo={bandaId}
          seleccion={seleccion} setSeleccion={setSeleccion}
          adaptador={adaptador}
          etiquetaRack="Internet" />

        {paso.id === 'calcular' && (
          r ? <Resultado r={r} notas={notas} onGuardar={onGuardar} />
            : <div className="empty">
                <b>Todavía no se puede calcular.</b>
                <p>Falta cerrar el trazo, ubicar dónde llega el internet, colocar al menos
                   un punto de acceso o escribir los metros construidos.</p>
              </div>
        )}
      </main>

      <div className="fab"><div className="in">
        <button className="gh" onClick={() => setI(Math.max(i - 1, 0))} disabled={i === 0}
          aria-label="Paso anterior">←</button>
        <button onClick={() => setI(Math.min(i + 1, GUIA_WIFI.pasos.length - 1))}
          disabled={!!falta || i === GUIA_WIFI.pasos.length - 1}>
          {falta || (i === GUIA_WIFI.pasos.length - 1 ? 'Listo' : 'Siguiente')}
        </button>
      </div></div>
    </div>
  )
}

function Resultado ({ r, notas, onGuardar }) {
  const [auditando, setAuditando] = useState(false)

  return (
    <>
      <div className="sec">Cuántos equipos pide esta casa</div>

      {/* LOS DOS CRITERIOS, COMO EN EL CALIBRE DEL CONDUCTOR.
          Quien cotiza no puede saber cuál domina, y equivocarse en cualquiera entrega una
          red que decepciona: pocos equipos en casa grande deja zonas muertas, y un solo
          equipo con cuarenta aparatos deja una red lenta que «sí tiene señal». */}
      <div className="card">
        <div className="ln">
          <span>Por cobertura<span className="s" style={{ display: 'block' }}>
            para que la señal llegue a todos lados</span></span>
          <b className={r.recomendacion.manda === 'cobertura' ? 'grande' : undefined}>
            {r.recomendacion.porCobertura}
          </b>
        </div>
        <div className="ln">
          <span>Por capacidad<span className="s" style={{ display: 'block' }}>
            para que aguante los aparatos conectados</span></span>
          <b className={r.recomendacion.manda === 'capacidad' ? 'grande' : undefined}>
            {r.recomendacion.porCapacidad}
          </b>
        </div>
      </div>
      <div className="nota leible">{r.recomendacion.porque}</div>

      {/* SE COBRA LO QUE SE COLOCÓ EN EL PLANO, no lo que recomienda el cálculo, porque el
          cable y la energía salen de dónde quedó cada equipo. Cuando los dos números no
          coinciden hay que decirlo: si no, la pantalla dice «se proponen 1» arriba y la
          lista cobra 2 abajo, y quien la lee no sabe cuál creer. */}
      <div className="ln big">
        <span>El cálculo pide</span>
        <b>{r.recomendacion.cantidad} {r.recomendacion.cantidad === 1 ? 'punto' : 'puntos'} de acceso</b>
      </div>
      <div className="ln">
        <span>Colocaste en el plano</span>
        <b>{r.colocados}</b>
      </div>

      {r.faltan > 0 && (
        <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
          <b>Faltan {r.faltan} por ubicar.</b> Se está cobrando lo que está en el plano, así
          que la cotización se queda corta. Regresa a colocarlos para ver dónde caen y
          cuánto cable piden.
        </div>
      )}
      {r.colocados > r.recomendacion.cantidad && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          Pusiste <b>{r.colocados - r.recomendacion.cantidad}</b> más de los que exige el
          cálculo, y se están cobrando los {r.colocados}. No está mal: más equipos dan mejor
          señal y menos zonas al límite. Sólo tenlo presente si hay que ajustar el precio —
          es el renglón que se puede quitar sin dejar la casa sin cobertura.
        </div>
      )}

      <div className="sec">Calidad de la señal</div>
      <div className="ln">
        <span>Cobertura estimada en {r.banda.nombre}</span>
        <b>{r.cobertura.porcentaje.toFixed(0)}%</b>
      </div>
      <div className="ln">
        <span>Alcance por equipo</span>
        <b>{r.recomendacion.alcanceEstimado} m</b>
      </div>
      <div className="ln">
        <span>SNR en el borde</span>
        <b>{r.snr.valor} dB</b>
      </div>

      {/* El SNR es lo único medido, y es lo que más manda. Si sale mal, el diagnóstico
          tiene que decir qué NO hacer: subir la potencia empeora la interferencia. */}
      <div className={r.snr.sirveVoz ? 'nota leible' : 'warn leible'} style={{ marginTop: 'var(--e4)' }}>
        {r.snr.porque}
      </div>

      <div className="sec">Instalación</div>
      <div className="ln"><span>Cable UTP</span><b>{r.red.metros} m</b></div>
      <div className="ln"><span>Consumo PoE</span><b>{r.potencia} W</b></div>
      {r.red.fueraDeLimite > 0 && (
        <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
          <b>{r.red.fueraDeLimite} tramo(s) pasan de 100 m.</b> El cobre no llega más lejos.
          Hay que meter un switch intermedio o pasar a fibra, y eso se cotiza aparte.
        </div>
      )}

      <div className="sec">Material</div>
      <Materiales lista={r.materiales} auditando={auditando} />

      <button type="button" className="discreto" style={{ marginTop: 'var(--e5)' }}
        onClick={() => setAuditando(!auditando)} aria-expanded={auditando}>
        {auditando ? 'Ocultar de dónde sale cada número' : 'Ver de dónde sale cada número'}
      </button>

      {auditando && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <p style={{ marginTop: 0 }}>
            <b>Umbrales.</b> El borde de celda de <b>{BORDE_CELDA} dBm</b> y el SNR mínimo de{' '}
            <b>{SNR.datos} dB para datos y {SNR.voz} para voz</b> salen de la documentación
            de Cisco Meraki. Se usa el criterio del fabricante que se instala, no uno
            genérico.
          </p>
          <p>
            <b>Alcance.</b> Modelo de interiores de la recomendación <b>ITU-R P.1238</b>:
            la pérdida crece con <i>N·log(distancia)</i>, donde N depende del tipo de
            construcción. El coeficiente <b>ya incluye los muros interiores</b>, por eso se
            pregunta de qué son los muros en vez de contarlos en el plano.
          </p>
          <p>
            <b>Cuántos equipos.</b> El mayor de dos cuentas: el área que cubre cada equipo
            —un cuadrado inscrito en su círculo de alcance, no el círculo entero, porque las
            celdas se traslapan al embaldosar— y los aparatos entre los que atiende cada uno.
          </p>
          <p style={{ marginBottom: 0 }}>
            <b>Qué NO es esto.</b> Una predicción. El refrigerador de acero, el espejo del
            baño y el router del vecino no están en el plano. Sirve para saber cuántos
            equipos pedir y dónde ir a medir el día de la instalación.
          </p>
        </div>
      )}

      {notas && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <b>Lo que reportó el cliente:</b> {notas}
        </div>
      )}

      {onGuardar && (
        <button className="ib pri w" style={{ marginTop: 'var(--e6)' }}
          onClick={() => onGuardar(r)}>Agregar a la cotización</button>
      )}
    </>
  )
}
